/**
 * WHICH ROW ACTION IS OFFERED, AND FROM WHICH STATUS.
 *
 * This table is the whole of the author's side of the article lifecycle. Only EIGHT transition
 * rules carry `actor_role='author'` — author_create, author_submit, author_delete_draft,
 * author_withdraw, author_resubmit, author_pull, author_archive, author_unarchive — and NO AUTHOR
 * RULE LEAVES `published` EXCEPT `author_archive`. Submit and resubmit belong to the editor, so
 * what is left for a list row is the five below.
 *
 * WHY A TABLE AND NOT AN `if` IN THE ROW. Every one of these has a from-state the server enforces
 * and answers 409 when it is wrong, and an action offered from the wrong state is a click that can
 * only fail. Keeping the rule in one readable place is also what makes it checkable against
 * `ArticleStatus` — add a status upstream and `ARTICLE_STATUSES` in the api layer stops
 * compiling, which sends you here.
 *
 * WHAT IS DELIBERATELY NOT HERE:
 *   - Unpublish. An author cannot take their own article off the site; only an editor can, and
 *     `unpublished` is therefore a status an author can be in and never a status they can reach.
 *   - Restore from a version. There is deliberately no restore route — that was the v1 cut — so
 *     drawing one would promise an operation that does not exist.
 *   - Anything to do with a series. Ruled out for v1 (decision-log row 153 item 2): authors group
 *     related pieces with a tag.
 */
import type { ArticleListItem, ArticleStatus, PublicId } from '../../features/cabinet/api';
import type { ArticlesKey } from './articles-i18n';

export type ArticleActionKind = 'withdraw' | 'pull' | 'archive' | 'unarchive' | 'delete';

/** The words are catalogue keys (`list.actions`), translated where the control is drawn. */
export interface ArticleActionDefinition {
  readonly kind: ArticleActionKind;
  readonly label: ArticlesKey;
  /** The from-states the platform accepts. Anything else answers 409. */
  readonly from: readonly ArticleStatus[];
  /** True when there is no way back. Puts the action behind a confirmation. */
  readonly destructive: boolean;
  /** The confirmation's title and body. Present for every action that needs confirming. */
  readonly confirm?: { readonly title: ArticlesKey; readonly body: ArticlesKey };
  /** What the author gets, in their words. Shown on the control. */
  readonly hint: ArticlesKey;
}

export const ARTICLE_ACTIONS: readonly ArticleActionDefinition[] = [
  {
    kind: 'withdraw',
    label: 'list.actions.withdraw.label',
    from: ['in_review'],
    destructive: false,
    hint: 'list.actions.withdraw.hint',
    confirm: {
      title: 'list.actions.withdraw.confirmTitle',
      body: 'list.actions.withdraw.confirmBody',
    },
  },
  {
    kind: 'pull',
    label: 'list.actions.pull.label',
    from: ['scheduled'],
    destructive: false,
    hint: 'list.actions.pull.hint',
    confirm: {
      title: 'list.actions.pull.confirmTitle',
      body: 'list.actions.pull.confirmBody',
    },
  },
  {
    kind: 'archive',
    label: 'list.actions.archive.label',
    from: ['published'],
    destructive: false,
    hint: 'list.actions.archive.hint',
    confirm: {
      title: 'list.actions.archive.confirmTitle',
      body: 'list.actions.archive.confirmBody',
    },
  },
  {
    kind: 'unarchive',
    label: 'list.actions.unarchive.label',
    from: ['archived'],
    destructive: false,
    hint: 'list.actions.unarchive.hint',
  },
  {
    kind: 'delete',
    label: 'list.actions.delete.label',
    // The from-state here is not a status: it is "never published". See `isActionAvailable`.
    from: ['draft', 'changes_requested', 'in_review', 'scheduled'],
    destructive: true,
    hint: 'list.actions.delete.hint',
    confirm: {
      title: 'list.actions.delete.confirmTitle',
      body: 'list.actions.delete.confirmBody',
    },
  },
];

/**
 * Whether an action can be offered for a row.
 *
 * DELETE IS THE ONE THAT IS NOT A STATUS QUESTION. `deleteNeverPublishedDraft` refuses with
 * `DELETE_ONLY_NEVER_PUBLISHED_DRAFT` for anything that has EVER been published, and `publishedAt`
 * is "stamped on first entry to published and IMMUTABLE after" — un-archiving keeps the original
 * stamp. So a `draft` that was once published and later pulled back still carries the stamp, and
 * `publishedAt` rather than `status` is the honest test.
 */
export function isActionAvailable(
  definition: ArticleActionDefinition,
  article: ArticleListItem,
): boolean {
  if (!definition.from.includes(article.status)) {
    return false;
  }
  if (definition.kind === 'delete') {
    return article.publishedAt === undefined || article.publishedAt === null;
  }
  return true;
}

export function availableActions(article: ArticleListItem): readonly ArticleActionDefinition[] {
  return ARTICLE_ACTIONS.filter((definition) => isActionAvailable(definition, article));
}

/** Maps an action onto the operation that performs it. One call each; no batching, no chaining. */
export interface ArticleActionRunner {
  withdraw: (articleId: PublicId) => Promise<unknown>;
  pull: (articleId: PublicId) => Promise<unknown>;
  archive: (articleId: PublicId) => Promise<unknown>;
  unarchive: (articleId: PublicId) => Promise<unknown>;
  delete: (articleId: PublicId) => Promise<unknown>;
}

/*
 * Design 15 (the owner's live look at real cabinets, 2026-10-01): the Pull and Delete confirmations
 * no longer name "the platform" - plain words, the owner's rule. What each promises is unchanged.
 */
